/**
 * @fileoverview Clock for department-migration picker freshness.
 * @module system-web/services/departmentMigrationFreshness
 *
 * Responsibilities:
 * - Schedule the next picker recompute when a live department run's heartbeat will expire.
 * Dependencies: department-wide heartbeat threshold.
 * Architecture: Pure scheduling helper. The Dexie liveQuery stays a read; this clock lives outside it.
 * Notes: A stopped worker writes nothing. The page advances the clock once when the snapshot arrives; this helper then schedules only future expiries so an already-paused run cannot spin.
 */

import { STEP_HEARTBEAT_STALE_MS, isDepartmentWideRunLive } from './departmentWideStepper';

/**
 * @contract DepartmentRunHeartbeat
 * Purpose: The only run fields needed to know when a live department badge goes stale.
 * Fields: datasetKey, status, startedAt, heartbeatAt, updatedAt.
 * Rules: Non-department and terminal runs do not schedule a clock.
 * Sync: Read from the already-loaded Dexie run snapshot; no extra fetch.
 */
export interface DepartmentRunHeartbeat {
  datasetKey?: string;
  status?: string;
  startedAt?: string;
  heartbeatAt?: string;
  updatedAt?: string;
}

/**
 * @process nextDepartmentRunHeartbeatExpiry
 * Steps: Ignore terminal and non-department runs, then return the soonest expiry still ahead of the supplied clock.
 * Inputs: Department-wide run snapshots and the clock used by the current render.
 * Outputs: Epoch milliseconds for the next timeout, or null when every live run is already stale or none exist.
 * Failure Handling: Unparseable timestamps are skipped.
 * Idempotency: REQUIRED — same runs and clock always return the same expiry.
 */
export function nextDepartmentRunHeartbeatExpiry(
  runs: readonly DepartmentRunHeartbeat[],
  nowMs: number,
): number | null {
  let nextFuture: number | null = null;
  for (const run of runs) {
    if (run.datasetKey !== 'department-wide' || !isDepartmentWideRunLive(run.status)) continue;
    const reference = Date.parse(run.heartbeatAt ?? run.updatedAt ?? run.startedAt ?? '');
    if (!Number.isFinite(reference)) continue;
    const expiry = reference + STEP_HEARTBEAT_STALE_MS + 1;
    // Elapsed expiries are already visible to the supplied clock. Returning
    // them would schedule a zero-delay timeout on every render.
    if (expiry <= nowMs) continue;
    if (nextFuture === null || expiry < nextFuture) nextFuture = expiry;
  }
  return nextFuture;
}
